import React, { useState, useRef } from 'react';
import { X, Upload, Plus, Edit, Trash2, Palette, Save, Building, RotateCcw } from 'lucide-react';
import { useEventStore } from '../store/eventStore';
import { Venue, ColorPreset } from '../types';
import { API_CONFIG } from '../config/api';

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({ isOpen, onClose }) => {
  const { 
    settings, 
    venues, 
    colorPresets, 
    updateSettings, 
    uploadLogo, 
    addVenue, 
    updateVenue, 
    deleteVenue,
    resetToDefaults 
  } = useEventStore();
  
  const [activeTab, setActiveTab] = useState<'general' | 'venues' | 'colors' | 'database'>('general');
  const [storeName, setStoreName] = useState(settings.storeName);
  const [storeEmail, setStoreEmail] = useState(settings.storeEmail || '');
  const [storePhone, setStorePhone] = useState(settings.storePhone || '');
  const [storeAddress, setStoreAddress] = useState(settings.storeAddress || '');
  const [applicationTitle, setApplicationTitle] = useState(settings.applicationTitle);
  const [applicationSubtitle, setApplicationSubtitle] = useState(settings.applicationSubtitle);
  const [selectedVenue, setSelectedVenue] = useState(settings.selectedVenue || '');
  const [themeColor, setThemeColor] = useState(settings.themeColor);
  const [backgroundColor, setBackgroundColor] = useState(settings.backgroundColor);
  const [textColor, setTextColor] = useState(settings.textColor);
  const [highlightTextColor, setHighlightTextColor] = useState(settings.highlightTextColor);
  const [dbHost, setDbHost] = useState(settings.dbHost || '');
  const [dbUser, setDbUser] = useState(settings.dbUser || '');
  const [dbPassword, setDbPassword] = useState(settings.dbPassword || '');
  const [dbName, setDbName] = useState(settings.dbName || '');
  const [newVenue, setNewVenue] = useState<Partial<Venue>>({ name: '', type: '', color: '#6b7280', icon: '📅' });
  const [editingVenue, setEditingVenue] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const connectDatabase = async () => {
    if (!dbHost || !dbUser || !dbName) {
      alert('Please provide DB_HOST, DB_USER, and DB_NAME before connecting.');
      return false;
    }

    try {
      const response = await fetch(`${API_CONFIG.base}/api/database/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: dbHost,
          user: dbUser,
          password: dbPassword,
          database: dbName
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.details || error.error || 'Failed to connect to database');
      }

      alert('Database connected successfully.');
      return true;
    } catch (error) {
      console.error('Database connection error:', error);
      alert(`Database connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  };

  const handleImportSqlFile = async (file: File) => {
    if (!file) return;

    try {
      const sqlContent = await file.text();
      if (!sqlContent.trim()) {
        alert('Selected file is empty.');
        return;
      }

      const response = await fetch(`${API_CONFIG.base}/api/database/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: sqlContent
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.details || error.error || 'Failed to import SQL file');
      }

      alert('SQL file imported successfully.');
    } catch (error) {
      console.error('SQL import error:', error);
      alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleExportDatabase = async () => {
    try {
      const response = await fetch(`${API_CONFIG.base}/api/database/export`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.details || error.error || 'Failed to export database');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'event_manager.sql';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      alert('Database export started. Check your downloads.');
    } catch (error) {
      console.error('Database export error:', error);
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleSaveSettings = async () => {
    updateSettings({
      storeName,
      storeEmail,
      storePhone,
      storeAddress,
      applicationTitle,
      applicationSubtitle,
      selectedVenue: selectedVenue || undefined,
      themeColor,
      backgroundColor,
      textColor,
      highlightTextColor,
      dbHost,
      dbUser,
      dbPassword,
      dbName
    });

    if (dbHost && dbUser && dbName) {
      await connectDatabase();
    }

    onClose();
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      resetToDefaults();
      setStoreName('My Restaurant');
      setStoreEmail('');
      setStorePhone('');
      setStoreAddress('');
      setApplicationTitle('Event Manager');
      setApplicationSubtitle('Professional Hospitality Event Management');
      setSelectedVenue('');
      setThemeColor('#f59e0b');
      setBackgroundColor('#0f172a');
      setTextColor('#ffffff');
      setHighlightTextColor('#f59e0b');
      setDbHost('');
      setDbUser('');
      setDbPassword('');
      setDbName('');
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadLogo(file);
    }
  };

  const handleResetLogo = () => {
    updateSettings({ logo: '/assets/default-logo.png' });
    alert('Logo reset to default successfully!');
  };

  const handleAddVenue = () => {
    if (newVenue.name && newVenue.type && newVenue.color) {
      addVenue({
        name: newVenue.name,
        type: newVenue.type,
        color: newVenue.color,
        icon: newVenue.icon || '📅'
      });
      setNewVenue({ name: '', type: '', color: '#6b7280', icon: '📅' });
    }
  };

  const handleUpdateVenue = (id: string, updates: Partial<Venue>) => {
    updateVenue(id, updates);
    setEditingVenue(null);
  };

  const handleDeleteVenue = (id: string) => {
    if (window.confirm('Are you sure you want to delete this venue?')) {
      deleteVenue(id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
          <h2 className="text-xl font-semibold text-slate-800">Settings</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-140px)]">
          {/* Sidebar */}
          <div className="w-48 bg-slate-50 border-r border-slate-200 flex-shrink-0">
            <div className="p-4 space-y-1">
              <button
                onClick={() => setActiveTab('general')}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'general' 
                    ? 'bg-amber-100 text-amber-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Building className="h-4 w-4" />
                General
              </button>
              <button
                onClick={() => setActiveTab('venues')}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'venues' 
                    ? 'bg-amber-100 text-amber-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Edit className="h-4 w-4" />
                Venues
              </button>
              <button
                onClick={() => setActiveTab('colors')}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'colors' 
                    ? 'bg-amber-100 text-amber-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Palette className="h-4 w-4" />
                Theme
              </button>
              <button
                onClick={() => setActiveTab('database')}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'database' 
                    ? 'bg-amber-100 text-amber-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Building className="h-4 w-4" />
                Database
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="p-6 space-y-6">
                {/* Store Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Store Name
                  </label>
                  <input
                    type="text"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="My Restaurant"
                  />
                </div>

                {/* Store Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Store Email
                  </label>
                  <input
                    type="email"
                    value={storeEmail}
                    onChange={(e) => setStoreEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="info@myrestaurant.com"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    This email will appear on invoices
                  </p>
                </div>

                {/* Store Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Store Phone
                  </label>
                  <input
                    type="tel"
                    value={storePhone}
                    onChange={(e) => setStorePhone(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    This phone number will appear on invoices
                  </p>
                </div>

                {/* Store Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Store Address
                  </label>
                  <textarea
                    value={storeAddress}
                    onChange={(e) => setStoreAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="123 Main Street, City, State 12345"
                    rows={3}
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    This address will appear on invoices
                  </p>
                </div>

                {/* Application Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Application Title
                  </label>
                  <input
                    type="text"
                    value={applicationTitle}
                    onChange={(e) => setApplicationTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Event Manager"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    This appears as the main title in the header
                  </p>
                </div>

                {/* Application Subtitle */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Application Subtitle
                  </label>
                  <input
                    type="text"
                    value={applicationSubtitle}
                    onChange={(e) => setApplicationSubtitle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Professional Hospitality Event Management"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    This appears as the subtitle in the header
                  </p>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Logo
                  </label>
                  <div className="flex items-center gap-4">
                    {settings.logo && (
                      <img
                        src={settings.logo}
                        alt="Logo"
                        className="w-16 h-16 object-contain rounded-md border border-slate-200"
                      />
                    )}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-medium transition-colors flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Upload Logo
                    </button>
                    {settings.logo && (
                      <button
                        onClick={handleResetLogo}
                        className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-md font-medium transition-colors flex items-center gap-2"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Reset to Default
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Recommended size: 200x200px, PNG or JPG
                  </p>
                </div>

                {/* Selected Venue */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Default Active Venue
                  </label>
                  <select
                    value={selectedVenue}
                    onChange={(e) => setSelectedVenue(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">All Venues</option>
                    {venues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        {venue.icon} {venue.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-slate-500 mt-1">
                    When selected, this venue will be highlighted in the header
                  </p>
                </div>
              </div>
            )}

            {/* Venues Tab */}
            {activeTab === 'venues' && (
              <div className="p-6 space-y-6">
                {/* Add New Venue */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-800 mb-3">Add New Venue</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      placeholder="Venue Name"
                      value={newVenue.name || ''}
                      onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Type (e.g., private_dining)"
                      value={newVenue.type || ''}
                      onChange={(e) => setNewVenue({ ...newVenue, type: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={newVenue.color || '#6b7280'}
                        onChange={(e) => setNewVenue({ ...newVenue, color: e.target.value })}
                        className="w-12 h-10 rounded border border-slate-300"
                      />
                      <input
                        type="text"
                        placeholder="🍕"
                        value={newVenue.icon || ''}
                        onChange={(e) => setNewVenue({ ...newVenue, icon: e.target.value })}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center"
                      />
                    </div>
                    <button
                      onClick={handleAddVenue}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>
                </div>

                {/* Existing Venues */}
                <div className="space-y-3">
                  <h3 className="font-medium text-slate-800">Manage Venues</h3>
                  {venues.map((venue) => (
                    <div key={venue.id} className="bg-white border border-slate-200 rounded-lg p-4">
                      {editingVenue === venue.id ? (
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                          <input
                            type="text"
                            defaultValue={venue.name}
                            onBlur={(e) => handleUpdateVenue(venue.id, { name: e.target.value })}
                            className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          />
                          <input
                            type="text"
                            defaultValue={venue.type}
                            onBlur={(e) => handleUpdateVenue(venue.id, { type: e.target.value })}
                            className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          />
                          <input
                            type="color"
                            defaultValue={venue.color}
                            onChange={(e) => handleUpdateVenue(venue.id, { color: e.target.value })}
                            className="w-full h-10 rounded border border-slate-300"
                          />
                          <input
                            type="text"
                            defaultValue={venue.icon}
                            onBlur={(e) => handleUpdateVenue(venue.id, { icon: e.target.value })}
                            className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center"
                          />
                          <button
                            onClick={() => setEditingVenue(null)}
                            className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md text-sm"
                          >
                            Done
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: venue.color }}
                            />
                            <span className="text-lg">{venue.icon}</span>
                            <div>
                              <div className="font-medium text-slate-800">{venue.name}</div>
                              <div className="text-sm text-slate-500">{venue.type}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingVenue(venue.id)}
                              className="p-2 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {!['restaurant', 'bar', 'banquet'].includes(venue.id) && (
                              <button
                                onClick={() => handleDeleteVenue(venue.id)}
                                className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Colors Tab */}
            {activeTab === 'colors' && (
              <div className="p-6 space-y-6">
                {/* Theme Color */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Application Theme Color
                  </label>
                  <div className="grid grid-cols-6 gap-3">
                    {colorPresets.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => setThemeColor(preset.color)}
                        className={`relative p-3 rounded-lg border-2 transition-all ${
                          themeColor === preset.color
                            ? 'border-slate-400 ring-2 ring-slate-200'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div
                          className="w-full h-8 rounded"
                          style={{ backgroundColor: preset.color }}
                        />
                        <div className="text-xs text-slate-600 mt-1 text-center">
                          {preset.name}
                        </div>
                        {themeColor === preset.color && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-slate-600 rounded-full border-2 border-white" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <input
                      type="color"
                      value={themeColor}
                      onChange={(e) => setThemeColor(e.target.value)}
                      className="w-20 h-10 rounded border border-slate-300"
                    />
                    <span className="ml-3 text-sm text-slate-600">Custom Color: {themeColor}</span>
                  </div>
                </div>

                {/* Background Color */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Application Background Color
                  </label>
                  <div className="grid grid-cols-6 gap-3">
                    {colorPresets.map((preset) => (
                      <button
                        key={`bg-${preset.id}`}
                        onClick={() => setBackgroundColor(preset.color)}
                        className={`relative p-3 rounded-lg border-2 transition-all ${
                          backgroundColor === preset.color
                            ? 'border-slate-400 ring-2 ring-slate-200'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div
                          className="w-full h-8 rounded"
                          style={{ backgroundColor: preset.color }}
                        />
                        <div className="text-xs text-slate-600 mt-1 text-center">
                          {preset.name}
                        </div>
                        {backgroundColor === preset.color && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-slate-600 rounded-full border-2 border-white" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-20 h-10 rounded border border-slate-300"
                    />
                    <span className="ml-3 text-sm text-slate-600">Custom Background: {backgroundColor}</span>
                  </div>
                </div>

                {/* Text Color */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Text Color
                  </label>
                  <div className="grid grid-cols-6 gap-3">
                    {colorPresets.map((preset) => (
                      <button
                        key={`text-${preset.id}`}
                        onClick={() => setTextColor(preset.color)}
                        className={`relative p-3 rounded-lg border-2 transition-all ${
                          textColor === preset.color
                            ? 'border-slate-400 ring-2 ring-slate-200'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div
                          className="w-full h-8 rounded"
                          style={{ backgroundColor: preset.color }}
                        />
                        <div className="text-xs text-slate-600 mt-1 text-center">
                          {preset.name}
                        </div>
                        {textColor === preset.color && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-slate-600 rounded-full border-2 border-white" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-20 h-10 rounded border border-slate-300"
                    />
                    <span className="ml-3 text-sm text-slate-600">Custom Text Color: {textColor}</span>
                  </div>
                </div>

                {/* Highlight Text Color */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Highlight Text Color
                  </label>
                  <div className="grid grid-cols-6 gap-3">
                    {colorPresets.map((preset) => (
                      <button
                        key={`highlight-${preset.id}`}
                        onClick={() => setHighlightTextColor(preset.color)}
                        className={`relative p-3 rounded-lg border-2 transition-all ${
                          highlightTextColor === preset.color
                            ? 'border-slate-400 ring-2 ring-slate-200'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div
                          className="w-full h-8 rounded"
                          style={{ backgroundColor: preset.color }}
                        />
                        <div className="text-xs text-slate-600 mt-1 text-center">
                          {preset.name}
                        </div>
                        {highlightTextColor === preset.color && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-slate-600 rounded-full border-2 border-white" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <input
                      type="color"
                      value={highlightTextColor}
                      onChange={(e) => setHighlightTextColor(e.target.value)}
                      className="w-20 h-10 rounded border border-slate-300"
                    />
                    <span className="ml-3 text-sm text-slate-600">Custom Highlight: {highlightTextColor}</span>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-800 mb-3">Theme Preview</h3>
                  <div 
                    className="w-full h-32 rounded-lg flex items-center justify-center font-medium relative overflow-hidden"
                    style={{ backgroundColor: backgroundColor }}
                  >
                    <div 
                      className="absolute top-2 right-2 px-3 py-1 rounded text-sm"
                      style={{ backgroundColor: themeColor, color: backgroundColor }}
                    >
                      Theme Color
                    </div>
                    <div className="text-center">
                      <div 
                        className="text-xl font-semibold"
                        style={{ color: textColor }}
                      >
                        {applicationTitle || 'Event Manager'}
                      </div>
                      <div 
                        className="text-sm opacity-80 mt-1"
                        style={{ color: textColor }}
                      >
                        {applicationSubtitle || 'Professional Hospitality Event Management'}
                      </div>
                      <div 
                        className="text-sm mt-2 font-medium"
                        style={{ color: highlightTextColor }}
                      >
                        Highlight Color Sample
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'database' && (
              <div className="p-6 space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">SQL Database Connection</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Enter your SQL database credentials and link them to the application.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">DB_HOST</label>
                      <input
                        type="text"
                        value={dbHost}
                        onChange={(e) => setDbHost(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="localhost"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">DB_USER</label>
                      <input
                        type="text"
                        value={dbUser}
                        onChange={(e) => setDbUser(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="root"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">DB_PASSWORD</label>
                      <input
                        type="password"
                        value={dbPassword}
                        onChange={(e) => setDbPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">DB_NAME</label>
                      <input
                        type="text"
                        value={dbName}
                        onChange={(e) => setDbName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="event_manager"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={connectDatabase}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium transition-colors"
                    >
                      Connect / Install
                    </button>
                    <button
                      onClick={() => importFileInputRef.current?.click()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                    >
                      Import SQL File
                    </button>
                    <button
                      onClick={handleExportDatabase}
                      className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-md font-medium transition-colors"
                    >
                      Export Database
                    </button>
                    <input
                      ref={importFileInputRef}
                      type="file"
                      accept=".sql"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                          handleImportSqlFile(file);
                          event.target.value = '';
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Note: These actions require backend implementation to interact with your SQL database.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <button
            onClick={handleResetToDefaults}
            className="px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors font-medium"
          >
            Reset to Defaults
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSettings}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};